import {
  A2A_SKILL_IDS,
  OPERATOR_STEP_SCHEMA_VERSION,
  skillMetadata,
} from "@erp/a2a-contract/v1/metadata";
import type { ContractSchemaDefinitions } from "@erp/a2a-contract/v1/types";
import { z } from "zod";

// "gpt" is the legacy OpenAI tier kept for stored configs — it resolves to the
// balanced GPT-6.1 Sol model. The stored "gpt-terra" and "gpt-sol" slugs
// also resolve to GPT-6.1 Sol.
// New configs should pick an explicit gpt-* tier.
export const OPERATOR_MODEL_TIERS = [
  "haiku",
  "sonnet",
  "opus",
  "gpt",
  "gpt-luna",
  "gpt-terra",
  "gpt-sol",
  "gpt-astra",
  "gemini",
] as const;

export type OperatorModelTier = (typeof OPERATOR_MODEL_TIERS)[number];

// "jev" selects the experimental Jev Driver (docs/operator/CONTEXT.md). It reads the page's
// DOM, so it is offered to the browser step only — never to remote desktop, whose page is a
// streamed canvas.
export const WEB_BROWSER_MODEL_TIERS = [
  "haiku",
  "sonnet",
  "opus",
  "gpt",
  "gpt-luna",
  "gpt-terra",
  "gpt-sol",
  "gpt-astra",
  "gemini",
  "jev",
] as const;
export type WebBrowserModelTier = (typeof WEB_BROWSER_MODEL_TIERS)[number];

export type RemoteDesktopExecuteModelTier = OperatorModelTier;
export const REMOTE_DESKTOP_EXECUTE_MODEL_TIERS = OPERATOR_MODEL_TIERS;

/** Canonical generated operator trace types; Python is the only shape source. */
export type OperatorStep = ContractSchemaDefinitions["OperatorStep"];
export type OperatorStepTokens = NonNullable<
  OperatorStep["generation"]["tokens"]
>;
export type OperatorStepAction = OperatorStep["actions"][number];
export { OPERATOR_STEP_SCHEMA_VERSION };

/** Tool type of the web-browser operator call (drives sub-agent rendering). */
export const OPERATOR_TOOL_TYPE = "agent.web_browser.execute" as const;

/**
 * Identity of the browser operator's A2A agent card, projected from the same
 * generated skill registry served by the Python service.
 */
const operatorSkillId = A2A_SKILL_IDS.browserUse;
const operatorSkillMetadata = skillMetadata[operatorSkillId];
export const OPERATOR_AGENT_CARD = {
  skillId: operatorSkillId,
  name: operatorSkillMetadata.name,
  description: operatorSkillMetadata.description,
  tags: operatorSkillMetadata.tags,
} as const;

export type OperatorAgentCard = {
  skillId: string;
  name: string;
  description: string;
  tags: readonly string[];
};

/**
 * Emitted when the operator halts early to request more context from the parent
 * agent. Shared output shape for the web-browser and remote-desktop operator tools.
 */
export const operatorNeedsContextSchema = z.object({
  question: z
    .string()
    .describe("What the operator needs the parent to provide"),
  operatorSummary: z
    .string()
    .describe(
      "Short recap of what the operator has done so far, suitable for seeding a resumed run",
    ),
});
export type OperatorNeedsContext = z.infer<typeof operatorNeedsContextSchema>;

// Validate saved operator evidence with the generated contract, never a mirrored DTO.
export { operatorStepSchema as OperatorStepSchema } from "@erp/a2a-contract/v1";
